import React, { useState, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import {
  MessageSquare, ArrowLeft, Send, Loader2, Info,
  Building2, Save, Check, Bell, Settings,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import api from "../utils/api";
import { usePublicAuth } from "../context/PublicAuthContext";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60)    return "Gerade eben";
  if (diff < 3600)  return `Vor ${Math.floor(diff / 60)} Min.`;
  if (diff < 86400) return `Vor ${Math.floor(diff / 3600)} Std.`;
  return `Vor ${Math.floor(diff / 86400)} Tagen`;
}

// ── Availability-Note Einstellungen ───────────────────────────────────────────
function AvailabilitySettings({ token }) {
  const [note, setNote]       = useState("");
  const [saved, setSaved]     = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/me/me", { headers: { Authorization: `Bearer ${token}` } });
        setNote(res.data?.messaging_availability_note || "");
      } catch {}
      setLoading(false);
    })();
  }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/messages/availability", { note }, { headers: { Authorization: `Bearer ${token}` } });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {}
    setSaving(false);
  };

  return (
    <div className="p-4 border-t" style={{ borderColor: "rgba(255,255,255,0.05)" }}>
      <div className="flex items-center gap-2 mb-3">
        <Settings size={13} className="text-amber-400 flex-shrink-0" />
        <p className="text-xs font-bold text-zinc-300">Abwesenheitsmeldung</p>
      </div>
      <p className="text-[11px] text-zinc-600 mb-2 leading-relaxed">
        Wird anderen Nutzern in eurer Unterhaltung angezeigt.
      </p>
      {loading ? (
        <div className="flex justify-center py-2">
          <Loader2 size={14} className="animate-spin text-zinc-700" />
        </div>
      ) : (
        <>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value.slice(0, 200))}
            placeholder="z. B. Bin bis Ende der Woche nicht erreichbar…"
            rows={3}
            data-testid="availability-note-input"
            className="w-full text-xs px-3 py-2 rounded-xl resize-none focus:outline-none focus:ring-1 transition"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              color: "#d4d4d8",
              focusRingColor: "rgba(245,158,11,0.3)",
            }}
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-[10px] text-zinc-700">{note.length}/200</span>
            <button
              onClick={save}
              disabled={saving}
              data-testid="availability-note-save"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-bold transition-all disabled:opacity-50"
              style={{
                background: saved ? "rgba(34,197,94,0.15)" : "rgba(245,158,11,0.12)",
                border: `1px solid ${saved ? "rgba(34,197,94,0.25)" : "rgba(245,158,11,0.2)"}`,
                color: saved ? "#4ade80" : "#fbbf24",
              }}
            >
              {saving ? (
                <Loader2 size={11} className="animate-spin" />
              ) : saved ? (
                <><Check size={11} /> Gespeichert</>
              ) : (
                <><Save size={11} /> Speichern</>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Thread-Listeneintrag ───────────────────────────────────────────────────────
function ThreadItem({ thread, active, onClick }) {
  return (
    <button
      onClick={() => onClick(thread)}
      data-testid={`messages-thread-${thread.id}`}
      className="w-full px-4 py-3 text-left transition-all flex items-start gap-3"
      style={{
        borderBottom: "1px solid rgba(255,255,255,0.03)",
        background: active
          ? "rgba(59,130,246,0.08)"
          : "transparent",
        borderLeft: active ? "2px solid #3b82f6" : "2px solid transparent",
      }}
    >
      <div className="relative flex-shrink-0">
        <div className="w-10 h-10 rounded-full overflow-hidden bg-zinc-800 flex items-center justify-center">
          {thread.other_avatar
            ? <img src={thread.other_avatar} alt="" className="w-full h-full object-cover" />
            : <span className="text-sm font-bold text-zinc-500">{thread.other_name?.[0]?.toUpperCase()}</span>}
        </div>
        {thread.other_is_label && (
          <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-emerald-600 flex items-center justify-center">
            <Building2 size={9} className="text-white" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-semibold text-white truncate">{thread.other_name}</p>
          <span className="text-[10px] text-zinc-600 flex-shrink-0">{timeAgo(thread.last_message_at)}</span>
        </div>
        <p className="text-zinc-500 text-xs truncate mt-0.5">{thread.last_message || "…"}</p>
        {thread.context_label && (
          <p className="text-[10px] text-zinc-700 truncate mt-0.5">
            {thread.context_type === "label_request" ? "🏷 " : "🤝 "}{thread.context_label}
          </p>
        )}
      </div>
      {thread.unread > 0 && (
        <span
          className="flex-shrink-0 min-w-[20px] h-[20px] rounded-full flex items-center justify-center text-[10px] font-black text-white px-1 mt-1"
          style={{ background: "linear-gradient(135deg,#3b82f6,#1d4ed8)" }}
        >
          {thread.unread > 9 ? "9+" : thread.unread}
        </span>
      )}
    </button>
  );
}

// ── Chat-Bereich ───────────────────────────────────────────────────────────────
function ThreadDetail({ threadId, onBack, currentUserId, token, onThreadRead }) {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending]     = useState(false);
  const messagesEndRef            = useRef(null);
  const textareaRef               = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/messages/threads/${threadId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
      onThreadRead?.();
    } catch {}
    setLoading(false);
  }, [threadId, token]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (data && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [data?.messages?.length]); // eslint-disable-line

  const sendReply = async () => {
    if (!replyText.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/messages/threads/${threadId}/reply`,
        { text: replyText.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReplyText("");
      await load();
      textareaRef.current?.focus();
    } catch {}
    setSending(false);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div
        className="flex items-center gap-3 px-4 py-3 flex-shrink-0"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
      >
        {/* Back button – only on mobile */}
        <button
          onClick={onBack}
          data-testid="thread-back-btn"
          className="lg:hidden p-1.5 rounded-xl hover:bg-white/[0.06] transition-colors text-zinc-400 hover:text-white flex-shrink-0"
        >
          <ArrowLeft size={16} />
        </button>
        {data && (
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-9 h-9 rounded-full overflow-hidden bg-zinc-800 flex-shrink-0 flex items-center justify-center">
              {data.other_info.avatar
                ? <img src={data.other_info.avatar} alt="" className="w-full h-full object-cover" />
                : <span className="text-sm font-bold text-zinc-500">{data.other_info.name?.[0]?.toUpperCase()}</span>}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-white truncate">{data.other_info.name}</p>
              {data.context_label && (
                <p className="text-[11px] text-zinc-600 truncate">
                  {data.context_type === "label_request" ? "🏷 " : "🤝 "}{data.context_label}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 size={20} className="animate-spin text-zinc-700" />
        </div>
      ) : data ? (
        <>
          {/* Availability note */}
          {data.availability_note && (
            <div
              className="mx-4 mt-3 px-3 py-2.5 rounded-xl flex items-start gap-2 flex-shrink-0"
              style={{
                background: "rgba(245,158,11,0.08)",
                border: "1px solid rgba(245,158,11,0.15)",
              }}
            >
              <Info size={13} className="text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-amber-300/80 text-xs leading-relaxed">{data.availability_note}</p>
            </div>
          )}

          {/* Nachrichten */}
          <div
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
            style={{ scrollbarWidth: "thin", scrollbarColor: "rgba(255,255,255,0.06) transparent" }}
          >
            {data.messages.length === 0 && (
              <div className="flex items-center justify-center h-full">
                <p className="text-zinc-600 text-sm">Noch keine Nachrichten</p>
              </div>
            )}
            {data.messages.map(m => (
              <div
                key={m.id}
                data-testid={`message-bubble-${m.id}`}
                className={`flex ${m.is_mine ? "justify-end" : "justify-start"} gap-2`}
              >
                {!m.is_mine && (
                  <div className="w-7 h-7 rounded-full overflow-hidden bg-zinc-800 flex-shrink-0 flex items-center justify-center mt-auto">
                    {m.sender_avatar
                      ? <img src={m.sender_avatar} alt="" className="w-full h-full object-cover" />
                      : <span className="text-[10px] font-bold text-zinc-500">{m.sender_name?.[0]?.toUpperCase()}</span>}
                  </div>
                )}
                <div className={`max-w-[72%] flex flex-col ${m.is_mine ? "items-end" : "items-start"}`}>
                  <div
                    className="px-4 py-2.5 rounded-2xl text-sm leading-relaxed break-words"
                    style={m.is_mine
                      ? { background: "rgba(59,130,246,0.22)", border: "1px solid rgba(59,130,246,0.3)", color: "#e0f2fe" }
                      : { background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.07)", color: "#d4d4d8" }
                    }
                  >
                    {m.text}
                  </div>
                  <p className="text-[10px] text-zinc-700 mt-1 px-1">{timeAgo(m.created_at)}</p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Reply */}
          <div
            className="px-4 pb-4 pt-3 flex-shrink-0"
            style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}
          >
            <div className="flex items-end gap-3">
              <div className="flex-1 relative">
                <textarea
                  ref={textareaRef}
                  value={replyText}
                  onChange={e => setReplyText(e.target.value.slice(0, 500))}
                  onKeyDown={e => {
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); }
                  }}
                  placeholder="Nachricht schreiben… (Enter senden, Shift+Enter neue Zeile)"
                  rows={2}
                  data-testid="messages-reply-input"
                  className="w-full text-sm px-4 py-3 rounded-2xl resize-none focus:outline-none focus:ring-1 focus:ring-blue-500/30 transition"
                  style={{
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    color: "#d4d4d8",
                  }}
                />
                <span className="absolute right-3 bottom-2.5 text-[10px] text-zinc-700">{replyText.length}/500</span>
              </div>
              <button
                onClick={sendReply}
                disabled={!replyText.trim() || sending}
                data-testid="messages-reply-send"
                className="w-10 h-10 flex items-center justify-center rounded-2xl transition-all flex-shrink-0 disabled:opacity-40 hover:scale-105"
                style={{
                  background: "rgba(59,130,246,0.25)",
                  border: "1px solid rgba(59,130,246,0.4)",
                }}
              >
                {sending
                  ? <Loader2 size={15} className="animate-spin text-blue-300" />
                  : <Send size={15} className="text-blue-300" />
                }
              </button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

// ── Hauptseite ─────────────────────────────────────────────────────────────────
export default function MessagesPage() {
  const { isPublicAuth, token, publicUser } = usePublicAuth();
  const [threads, setThreads]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [activeThread, setActiveThread] = useState(null);
  const [mobileView, setMobileView]   = useState("list"); // "list" | "detail"

  const canUseMessages = isPublicAuth && (
    publicUser?.artist_ids?.length > 0 ||
    publicUser?.artist_id ||
    publicUser?.is_label_manager
  );

  const fetchThreads = useCallback(async () => {
    if (!canUseMessages) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await api.get("/messages/threads", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setThreads(res.data || []);
    } catch {}
    setLoading(false);
  }, [canUseMessages, token]);

  useEffect(() => { fetchThreads(); }, [fetchThreads]);

  const openThread = (thread) => {
    setActiveThread(thread);
    setMobileView("detail");
    // Unread lokal sofort auf 0 setzen
    setThreads(prev =>
      prev.map(t => t.id === thread.id ? { ...t, unread: 0 } : t)
    );
  };

  const goBack = () => {
    setMobileView("list");
    fetchThreads();
  };

  const totalUnread = threads.reduce((s, t) => s + (t.unread || 0), 0);

  return (
    <div className="min-h-screen bg-[#060606] text-white flex flex-col">
      <Helmet>
        <title>Nachrichten – HUWND</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Header />

      <div className="flex-1 flex flex-col max-w-5xl w-full mx-auto px-4 sm:px-6 py-8 gap-0">

        {/* Seitentitel */}
        <div className="flex items-center gap-3 mb-6">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.2)" }}
          >
            <MessageSquare size={18} className="text-blue-400" />
          </div>
          <div>
            <h1 className="text-xl font-black text-white">Nachrichten</h1>
            {totalUnread > 0 && (
              <p className="text-xs text-blue-400 mt-0.5">{totalUnread} ungelesene Nachricht{totalUnread !== 1 ? "en" : ""}</p>
            )}
          </div>
        </div>

        {/* Nicht eingeloggt */}
        {!isPublicAuth && (
          <div
            className="rounded-2xl p-12 text-center"
            style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}
          >
            <Bell size={32} className="mx-auto mb-3 text-zinc-700" />
            <p className="text-zinc-400 text-sm mb-1 font-semibold">Nicht eingeloggt</p>
            <p className="text-zinc-600 text-xs">Bitte melde dich an, um deine Nachrichten zu lesen.</p>
          </div>
        )}

        {/* Kein Zugang */}
        {isPublicAuth && !canUseMessages && (
          <div
            className="rounded-2xl p-12 text-center"
            style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}
          >
            <MessageSquare size={32} className="mx-auto mb-3 text-zinc-700" />
            <p className="text-zinc-400 text-sm mb-1 font-semibold">Nachrichten nicht verfügbar</p>
            <p className="text-zinc-600 text-xs leading-relaxed max-w-xs mx-auto">
              Das Nachrichten-System steht nur verifizierten Artists und Label-Managern zur Verfügung.
            </p>
          </div>
        )}

        {/* Haupt-Layout */}
        {canUseMessages && (
          <div
            className="flex-1 rounded-2xl overflow-hidden flex"
            style={{
              background: "rgba(255,255,255,0.01)",
              border: "1px solid rgba(255,255,255,0.06)",
              minHeight: "calc(100vh - 280px)",
            }}
          >
            {/* ── Linke Spalte: Thread-Liste ───────────────────────── */}
            <div
              className={`
                ${mobileView === "detail" ? "hidden" : "flex"} lg:flex
                flex-col w-full lg:w-80 xl:w-96 flex-shrink-0
              `}
              style={{ borderRight: "1px solid rgba(255,255,255,0.05)" }}
            >
              {/* Thread-Liste Header */}
              <div
                className="px-4 py-3 flex-shrink-0"
                style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}
              >
                <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest">Gespräche</p>
              </div>

              {/* Thread-Liste */}
              <div
                className="flex-1 overflow-y-auto"
                style={{ scrollbarWidth: "thin", scrollbarColor: "rgba(255,255,255,0.06) transparent" }}
              >
                {loading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 size={20} className="animate-spin text-zinc-700" />
                  </div>
                ) : threads.length === 0 ? (
                  <div className="py-12 text-center px-6">
                    <MessageSquare size={28} className="mx-auto mb-3 text-zinc-800" />
                    <p className="text-zinc-500 text-sm font-semibold mb-1">Noch keine Gespräche</p>
                    <p className="text-zinc-700 text-xs leading-relaxed">
                      Schreib Artists oder Labels über den Collab Finder an.
                    </p>
                  </div>
                ) : (
                  threads.map(t => (
                    <ThreadItem
                      key={t.id}
                      thread={t}
                      active={activeThread?.id === t.id}
                      onClick={openThread}
                    />
                  ))
                )}
              </div>

              {/* Abwesenheitsmeldung */}
              <AvailabilitySettings token={token} />
            </div>

            {/* ── Rechte Spalte: Chat-Bereich ──────────────────────── */}
            <div
              className={`
                ${mobileView === "list" ? "hidden" : "flex"} lg:flex
                flex-col flex-1 min-w-0
              `}
            >
              {activeThread ? (
                <ThreadDetail
                  key={activeThread.id}
                  threadId={activeThread.id}
                  onBack={goBack}
                  token={token}
                  currentUserId={publicUser?.auth0_id}
                  onThreadRead={fetchThreads}
                />
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
                    style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.12)" }}
                  >
                    <MessageSquare size={24} className="text-blue-400/50" />
                  </div>
                  <p className="text-zinc-500 text-sm font-semibold mb-1">Gespräch auswählen</p>
                  <p className="text-zinc-700 text-xs leading-relaxed max-w-xs">
                    Wähle links ein Gespräch aus, um die Nachrichten zu lesen.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
